/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import SendIcon from '@atlaskit/icon/core/send';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';

const styles = cssMap({
	sendButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		border: 'none',
		borderRadius: token('radius.medium'),
		cursor: 'pointer',
		backgroundColor: token('color.background.brand.bold'),
		'&:hover': {
			backgroundColor: token('color.background.brand.bold.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.brand.bold.pressed'),
			transition: token('motion.button.pressed'),
		},
		transition: token('motion.button.hovered'),
	},
	sendButtonDisabled: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		border: 'none',
		borderRadius: token('radius.medium'),
		cursor: 'not-allowed',
		backgroundColor: token('color.background.disabled'),
	},
});

type SubmitButtonProps = {
	onClick: () => void;
	disabled?: boolean;
	testId?: string;
};

export const SubmitButton = ({
	onClick,
	disabled = false,
	testId = 'submit-button',
}: SubmitButtonProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const label = formatMessage(messages.sendMessageLabel);

	return (
		<Pressable
			onClick={onClick}
			isDisabled={disabled}
			aria-label={label}
			xcss={disabled ? styles.sendButtonDisabled : styles.sendButton}
			testId={testId}
		>
			<SendIcon
				label={label}
				color={disabled ? token('color.icon.disabled') : token('color.icon.inverse')}
				testId={`${testId}-icon`}
			/>
		</Pressable>
	);
};
